import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import Navbar from '../layout/Navbar';
import Preloader from '../layout/preloader';
import Footer from '../section-pages/footer';
import ScrollToTopBtn from '../layout/ScrollToTop';
import ApiRanking from '../services/rankingService';

const ABAS = {
  GYEORUGI: 'gyeorugi',
  POOMSAE: 'poomsae',
  ACADEMIAS: 'academias',
};

const FILTROS_GYEORUGI_INICIAIS = {
  sexo: '',
  faixa: '',
  peso: '',
};

const FILTROS_POOMSAE_INICIAIS = {
  categoria: '',
  faixa: '',
};

const obterTodasEtapas = (grupos = []) => {
  const etapas = new Set();

  grupos.forEach((grupo) => {
    (grupo.atletas || []).forEach((atleta) => {
      Object.keys(atleta.pontos_etapas || {}).forEach((etapa) => {
        etapas.add(Number(etapa));
      });
    });
  });

  return Array.from(etapas).sort((a, b) => a - b);
};

const montarLinhasRanking = (grupos = []) => {
  return grupos.flatMap((grupo, grupoIndex) =>
    (grupo.atletas || []).map((atleta, atletaIndex) => ({
      ...atleta,
      grupoIndex,
      atletaIndex,
      categoria: grupo.categoria,
      divisao: grupo.divisao,
      graduacao: grupo.graduacao,
      faixa: grupo.faixa,
      sexo: grupo.sexo,
      peso: grupo.peso,
    }))
  );
};

export default function Ranking() {
  const [anos, setAnos] = useState([]);
  const [anoSelecionado, setAnoSelecionado] = useState('');
  const [abaAtiva, setAbaAtiva] = useState(ABAS.GYEORUGI);

  const [rankingGyeorugi, setRankingGyeorugi] = useState([]);
  const [rankingPoomsae, setRankingPoomsae] = useState([]);
  const [rankingAcademias, setRankingAcademias] = useState([]);

  const [competicaoGyeorugi, setCompeticaoGyeorugi] = useState(null);
  const [competicaoPoomsae, setCompeticaoPoomsae] = useState(null);

  const [opcoesFiltrosGyeorugi, setOpcoesFiltrosGyeorugi] = useState({
    generos: [],
    categorias: [],
    pesos: [],
  });

  const [opcoesFiltrosPoomsae, setOpcoesFiltrosPoomsae] = useState({
    categoriasEtarias: [],
    faixas: [],
  });

  const [filtrosGyeorugi, setFiltrosGyeorugi] = useState(
    FILTROS_GYEORUGI_INICIAIS
  );

  const [filtrosPoomsae, setFiltrosPoomsae] = useState(
    FILTROS_POOMSAE_INICIAIS
  );

  const [isLoadingAnos, setIsLoadingAnos] = useState(true);
  const [isLoadingRanking, setIsLoadingRanking] = useState(false);

  const etapasGyeorugi = useMemo(
    () => obterTodasEtapas(rankingGyeorugi),
    [rankingGyeorugi]
  );

  const etapasPoomsae = useMemo(
    () => obterTodasEtapas(rankingPoomsae),
    [rankingPoomsae]
  );

  const linhasGyeorugi = useMemo(
    () => montarLinhasRanking(rankingGyeorugi),
    [rankingGyeorugi]
  );

  const linhasPoomsae = useMemo(
    () => montarLinhasRanking(rankingPoomsae),
    [rankingPoomsae]
  );

  useEffect(() => {
    esconderPreloader();
    buscarAnos();
  }, []);

  useEffect(() => {
    if (!anoSelecionado) {
      limparDadosDosRankings();
      return;
    }

    if (abaAtiva === ABAS.GYEORUGI) {
      setFiltrosGyeorugi(FILTROS_GYEORUGI_INICIAIS);
      buscarFiltrosGyeorugi(anoSelecionado);
      return;
    }

    if (abaAtiva === ABAS.POOMSAE) {
      setFiltrosPoomsae(FILTROS_POOMSAE_INICIAIS);
      buscarFiltrosPoomsae(anoSelecionado);
      return;
    }

    if (abaAtiva === ABAS.ACADEMIAS) {
      buscarRankingAcademias(anoSelecionado);
    }
  }, [anoSelecionado, abaAtiva]);

  useEffect(() => {
    if (
      !anoSelecionado ||
      abaAtiva !== ABAS.GYEORUGI
    ) {
      return;
    }

    buscarRankingGyeorugi();
  }, [
    anoSelecionado,
    abaAtiva,
    filtrosGyeorugi.sexo,
    filtrosGyeorugi.faixa,
    filtrosGyeorugi.peso,
  ]);

  useEffect(() => {
    if (
      !anoSelecionado ||
      abaAtiva !== ABAS.POOMSAE
    ) {
      return;
    }

    buscarRankingPoomsae();
  }, [
    anoSelecionado,
    abaAtiva,
    filtrosPoomsae.categoria,
    filtrosPoomsae.faixa,
  ]);

  const esconderPreloader = () => {
    if (typeof window === 'undefined') {
      return;
    }

    const loader = document.getElementById('mainpreloader');

    if (!loader) {
      return;
    }

    setTimeout(() => {
      loader.classList.add('fadeOut');
      loader.style.display = 'none';
    }, 600);
  };

  const limparDadosDosRankings = () => {
    setRankingGyeorugi([]);
    setRankingPoomsae([]);
    setRankingAcademias([]);

    setCompeticaoGyeorugi(null);
    setCompeticaoPoomsae(null);
  };

  const buscarAnos = async () => {
    try {
      setIsLoadingAnos(true);

      const response = await ApiRanking.listarAnos();
      const anosDisponiveis = response.data || [];

      setAnos(anosDisponiveis);

      if (anosDisponiveis.length > 0) {
        setAnoSelecionado(String(anosDisponiveis[0]));
      }
    } catch (error) {
      console.error('Erro ao buscar anos:', error);
      setAnos([]);
    } finally {
      setIsLoadingAnos(false);
    }
  };

  const buscarFiltrosGyeorugi = async (ano) => {
    try {
      const response = await ApiRanking.listarFiltros(
        ano,
        ABAS.GYEORUGI
      );

      setOpcoesFiltrosGyeorugi({
        generos: response.data?.generos || [],
        categorias: response.data?.categorias || [],
        pesos: response.data?.pesos || [],
      });
    } catch (error) {
      console.error(
        'Erro ao buscar filtros de Gyeorugi:',
        error
      );

      setOpcoesFiltrosGyeorugi({
        generos: [],
        categorias: [],
        pesos: [],
      });
    }
  };

  const buscarFiltrosPoomsae = async (ano) => {
    try {
      const response = await ApiRanking.listarFiltros(
        ano,
        ABAS.POOMSAE
      );

      setOpcoesFiltrosPoomsae({
        categoriasEtarias:
          response.data?.categorias_etarias ||
          response.data?.categorias ||
          [],

        faixas:
          response.data?.faixas ||
          response.data?.categorias_faixa ||
          [],
      });
    } catch (error) {
      console.error(
        'Erro ao buscar filtros de Poomsae:',
        error
      );

      setOpcoesFiltrosPoomsae({
        categoriasEtarias: [],
        faixas: [],
      });
    }
  };

  const buscarRankingGyeorugi = async () => {
    try {
      setIsLoadingRanking(true);

      const response = await ApiRanking.listarPorAno(
        anoSelecionado,
        ABAS.GYEORUGI,
        filtrosGyeorugi
      );

      setCompeticaoGyeorugi(
        response.data?.competicao || null
      );

      setRankingGyeorugi(
        response.data?.ranking || []
      );
    } catch (error) {
      console.error(
        'Erro ao buscar ranking de Gyeorugi:',
        error
      );

      setCompeticaoGyeorugi(null);
      setRankingGyeorugi([]);
    } finally {
      setIsLoadingRanking(false);
    }
  };

  const buscarRankingPoomsae = async () => {
    try {
      setIsLoadingRanking(true);

      const response = await ApiRanking.listarPorAno(
        anoSelecionado,
        ABAS.POOMSAE,
        filtrosPoomsae
      );

      setCompeticaoPoomsae(
        response.data?.competicao || null
      );

      setRankingPoomsae(
        response.data?.ranking || []
      );
    } catch (error) {
      console.error(
        'Erro ao buscar ranking de Poomsae:',
        error
      );

      setCompeticaoPoomsae(null);
      setRankingPoomsae([]);
    } finally {
      setIsLoadingRanking(false);
    }
  };

  const buscarRankingAcademias = async (ano) => {
    if (!ano) {
      setRankingAcademias([]);
      return;
    }

    try {
      setIsLoadingRanking(true);

      const response = await ApiRanking.listarAcademiasPorAno(
        ano
      );

      if (
        response.statusRetorno &&
        response.statusRetorno !== 200
      ) {
        console.error(
          'Erro ao buscar ranking de academias:',
          response.message
        );

        setRankingAcademias([]);
        return;
      }

      setRankingAcademias(response.data || []);
    } catch (error) {
      console.error(
        'Erro ao buscar ranking de academias:',
        error
      );

      setRankingAcademias([]);
    } finally {
      setIsLoadingRanking(false);
    }
  };

  const alterarFiltroGyeorugi = (campo, valor) => {
    setFiltrosGyeorugi((filtrosAtuais) => ({
      ...filtrosAtuais,
      [campo]: valor,
    }));
  };

  const alterarFiltroPoomsae = (campo, valor) => {
    setFiltrosPoomsae((filtrosAtuais) => ({
      ...filtrosAtuais,
      [campo]: valor,
    }));
  };

  const limparFiltrosGyeorugi = () => {
    setFiltrosGyeorugi(FILTROS_GYEORUGI_INICIAIS);
  };

  const limparFiltrosPoomsae = () => {
    setFiltrosPoomsae(FILTROS_POOMSAE_INICIAIS);
  };

  const classeClassificacao = (classificacao) => {
    if (
      classificacao === 'Campeão' ||
      classificacao === 'Campeã'
    ) {
      return 'campeao';
    }

    if (
      classificacao === 'Vice-Campeão' ||
      classificacao === 'Vice-Campeã'
    ) {
      return 'vice-campeao';
    }

    if (classificacao === '3º Lugar') {
      return 'terceiro-lugar';
    }

    return '';
  };

  const classeClassificacaoAcademia = (posicao) => {
    if (Number(posicao) === 1) {
      return 'campeao';
    }

    if (Number(posicao) === 2) {
      return 'vice-campeao';
    }

    if (Number(posicao) === 3) {
      return 'terceiro-lugar';
    }

    return '';
  };

  const nomePosicaoAcademia = (posicao) => {
    return `${posicao}º`;
  };

  const obterCategoriaEtariaPoomsae = (atleta) => {
    return atleta.categoria || atleta.divisao || '-';
  };

  const obterFaixaPoomsae = (atleta) => {
    return atleta.faixa || atleta.graduacao || '-';
  };

  const imprimir = () => {
    window.print();
  };

  const renderizarAbas = () => {
    if (!anoSelecionado) {
      return null;
    }

    return (
      <div className="ranking-tabs">
        <button
          type="button"
          className={
            abaAtiva === ABAS.GYEORUGI
              ? 'active'
              : ''
          }
          onClick={() => setAbaAtiva(ABAS.GYEORUGI)}
        >
          Gyeorugi
        </button>

        <button
          type="button"
          className={
            abaAtiva === ABAS.POOMSAE
              ? 'active'
              : ''
          }
          onClick={() => setAbaAtiva(ABAS.POOMSAE)}
        >
          Poomsae
        </button>

        <button
          type="button"
          className={
            abaAtiva === ABAS.ACADEMIAS
              ? 'active'
              : ''
          }
          onClick={() => setAbaAtiva(ABAS.ACADEMIAS)}
        >
          Academias
        </button>
      </div>
    );
  };

  const renderizarFiltrosGyeorugi = () => {
    if (abaAtiva !== ABAS.GYEORUGI) {
      return null;
    }

    return (
      <div className="ranking-filter-area">
        <div className="ranking-filter-grid">
          <div className="ranking-filter-field">
            <label htmlFor="ranking-genero">
              Gênero
            </label>

            <select
              id="ranking-genero"
              value={filtrosGyeorugi.sexo}
              onChange={(event) =>
                alterarFiltroGyeorugi(
                  'sexo',
                  event.target.value
                )
              }
            >
              <option value="">Todos</option>

              {opcoesFiltrosGyeorugi.generos.map(
                (genero) => (
                  <option
                    key={genero}
                    value={genero}
                  >
                    {genero}
                  </option>
                )
              )}
            </select>
          </div>

          <div className="ranking-filter-field">
            <label htmlFor="ranking-categoria-gyeorugi">
              Categoria
            </label>

            <select
              id="ranking-categoria-gyeorugi"
              value={filtrosGyeorugi.faixa}
              onChange={(event) =>
                alterarFiltroGyeorugi(
                  'faixa',
                  event.target.value
                )
              }
            >
              <option value="">Todas</option>

              {opcoesFiltrosGyeorugi.categorias.map(
                (categoria) => (
                  <option
                    key={categoria}
                    value={categoria}
                  >
                    {categoria}
                  </option>
                )
              )}
            </select>
          </div>

          <div className="ranking-filter-field">
            <label htmlFor="ranking-peso">
              Peso
            </label>

            <select
              id="ranking-peso"
              value={filtrosGyeorugi.peso}
              onChange={(event) =>
                alterarFiltroGyeorugi(
                  'peso',
                  event.target.value
                )
              }
            >
              <option value="">Todos</option>

              {opcoesFiltrosGyeorugi.pesos.map(
                (peso) => (
                  <option
                    key={peso}
                    value={peso}
                  >
                    {peso}
                  </option>
                )
              )}
            </select>
          </div>
        </div>

        <div className="ranking-filter-actions">
          <button
            type="button"
            className="ranking-btn ranking-btn-secondary"
            onClick={limparFiltrosGyeorugi}
          >
            Limpar filtros
          </button>

          <button
            type="button"
            className="ranking-btn ranking-btn-primary"
            onClick={imprimir}
          >
            Imprimir ranking
          </button>
        </div>
      </div>
    );
  };

  const renderizarFiltrosPoomsae = () => {
    if (abaAtiva !== ABAS.POOMSAE) {
      return null;
    }

    return (
      <div className="ranking-filter-area">
        <div className="ranking-filter-grid ranking-filter-grid-poomsae">
          <div className="ranking-filter-field">
            <label htmlFor="ranking-categoria-poomsae">
              Categoria etária
            </label>

            <select
              id="ranking-categoria-poomsae"
              value={filtrosPoomsae.categoria}
              onChange={(event) =>
                alterarFiltroPoomsae(
                  'categoria',
                  event.target.value
                )
              }
            >
              <option value="">Todas</option>

              {opcoesFiltrosPoomsae.categoriasEtarias.map(
                (categoria) => (
                  <option
                    key={categoria}
                    value={categoria}
                  >
                    {categoria}
                  </option>
                )
              )}
            </select>
          </div>

          <div className="ranking-filter-field">
            <label htmlFor="ranking-faixa-poomsae">
              Faixa
            </label>

            <select
              id="ranking-faixa-poomsae"
              value={filtrosPoomsae.faixa}
              onChange={(event) =>
                alterarFiltroPoomsae(
                  'faixa',
                  event.target.value
                )
              }
            >
              <option value="">Todas</option>

              {opcoesFiltrosPoomsae.faixas.map(
                (faixa) => (
                  <option
                    key={faixa}
                    value={faixa}
                  >
                    {faixa}
                  </option>
                )
              )}
            </select>
          </div>
        </div>

        <div className="ranking-filter-actions">
          <button
            type="button"
            className="ranking-btn ranking-btn-secondary"
            onClick={limparFiltrosPoomsae}
          >
            Limpar filtros
          </button>

          <button
            type="button"
            className="ranking-btn ranking-btn-primary"
            onClick={imprimir}
          >
            Imprimir ranking
          </button>
        </div>
      </div>
    );
  };

  const renderizarAcoesAcademias = () => {
    if (abaAtiva !== ABAS.ACADEMIAS) {
      return null;
    }

    return (
      <div className="ranking-filter-area ranking-academias-actions">
        <div />

        <div className="ranking-filter-actions">
          <button
            type="button"
            className="ranking-btn ranking-btn-primary"
            onClick={imprimir}
          >
            Imprimir ranking
          </button>
        </div>
      </div>
    );
  };

  const renderizarEstadoTabela = (linhas, mensagemVazia) => {
    if (isLoadingRanking || isLoadingAnos) {
      return (
        <p className="text-center">
          Carregando ranking...
        </p>
      );
    }

    if (!linhas.length) {
      return (
        <p className="text-center">
          {mensagemVazia}
        </p>
      );
    }

    return null;
  };

  const renderizarTabelaGyeorugi = () => {
    const estado = renderizarEstadoTabela(
      linhasGyeorugi,
      'Nenhum resultado encontrado.'
    );

    if (estado) {
      return estado;
    }

    return (
      <div className="ranking-table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Classificação</th>
              <th>Atleta</th>
              <th>Academia</th>
              <th>Faixa</th>
              <th>Gênero</th>
              <th>Peso</th>

              {etapasGyeorugi.map((etapa) => (
                <th key={etapa}>
                  {etapa}ª Etapa
                </th>
              ))}

              <th>Total</th>
            </tr>
          </thead>

          <tbody>
            {linhasGyeorugi.map((atleta) => (
              <tr
                key={[
                  atleta.aluno_id ||
                    atleta.aluno_temp_id ||
                    atleta.atleta_nome,
                  atleta.grupoIndex,
                  atleta.atletaIndex,
                ].join('-')}
              >
                <td
                  className={classeClassificacao(
                    atleta.classificacao
                  )}
                >
                  {atleta.classificacao}
                </td>

                <td>{atleta.atleta_nome}</td>
                <td>{atleta.academia_nome}</td>

                <td>
                  {atleta.graduacao ||
                    atleta.faixa ||
                    '-'}
                </td>

                <td>{atleta.sexo || '-'}</td>
                <td>{atleta.peso || '-'}</td>

                {etapasGyeorugi.map((etapa) => (
                  <td key={etapa}>
                    {atleta.pontos_etapas?.[etapa] || 0}
                  </td>
                ))}

                <td className="ranking-total">
                  {atleta.total || 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderizarTabelaPoomsae = () => {
    const estado = renderizarEstadoTabela(
      linhasPoomsae,
      'Nenhum resultado encontrado.'
    );

    if (estado) {
      return estado;
    }

    return (
      <div className="ranking-table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Classificação</th>
              <th>Atleta</th>
              <th>Academia</th>
              <th>Categoria etária</th>
              <th>Faixa</th>

              {etapasPoomsae.map((etapa) => (
                <th key={etapa}>
                  {etapa}ª Etapa
                </th>
              ))}

              <th>Total</th>
            </tr>
          </thead>

          <tbody>
            {linhasPoomsae.map((atleta) => (
              <tr
                key={[
                  atleta.aluno_id ||
                    atleta.aluno_temp_id ||
                    atleta.atleta_nome,
                  atleta.grupoIndex,
                  atleta.atletaIndex,
                ].join('-')}
              >
                <td
                  className={classeClassificacao(
                    atleta.classificacao
                  )}
                >
                  {atleta.classificacao}
                </td>

                <td>{atleta.atleta_nome}</td>
                <td>{atleta.academia_nome}</td>

                <td>
                  {obterCategoriaEtariaPoomsae(atleta)}
                </td>

                <td>
                  {obterFaixaPoomsae(atleta)}
                </td>

                {etapasPoomsae.map((etapa) => (
                  <td key={etapa}>
                    {atleta.pontos_etapas?.[etapa] || 0}
                  </td>
                ))}

                <td className="ranking-total">
                  {atleta.total || 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderizarTabelaAcademias = () => {
    const estado = renderizarEstadoTabela(
      rankingAcademias,
      'Nenhuma academia encontrada.'
    );

    if (estado) {
      return estado;
    }

    return (
      <div className="ranking-table-wrapper ranking-academias-table">
        <table>
          <thead>
            <tr>
              <th>Rank</th>
              <th>Academia</th>
              <th>Atletas</th>
              <th>Pontuação</th>
            </tr>
          </thead>

          <tbody>
            {rankingAcademias.map((academia) => (
              <tr
                key={
                  academia.academia_id ||
                  `${academia.academia_nome}-${academia.classificacao}`
                }
              >
                <td
                  className={classeClassificacaoAcademia(
                    academia.classificacao
                  )}
                >
                  {nomePosicaoAcademia(
                    academia.classificacao
                  )}
                </td>

                <td>
                  {academia.academia_nome}
                </td>

                <td>
                  {academia.total_atletas || 0}
                </td>

                <td className="ranking-total">
                  {academia.pontuacao || 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderizarConteudoGyeorugi = () => {
    if (abaAtiva !== ABAS.GYEORUGI) {
      return null;
    }

    return (
      <div className="ranking-content">
        <div className="ranking-heading">
          <h1>
            Classificação Geral Detalhada - Gyeorugi
          </h1>

          <p>
            {competicaoGyeorugi?.nome ||
              'Ranking Estadual de Gyeorugi'}

            {anoSelecionado
              ? ` - ${anoSelecionado}`
              : ''}
          </p>
        </div>

        {renderizarTabelaGyeorugi()}
      </div>
    );
  };

  const renderizarConteudoPoomsae = () => {
    if (abaAtiva !== ABAS.POOMSAE) {
      return null;
    }

    return (
      <div className="ranking-content">
        <div className="ranking-heading">
          <h1>
            Classificação Geral Detalhada - Poomsae
          </h1>

          <p>
            {competicaoPoomsae?.nome ||
              'Ranking Estadual de Poomsae'}

            {anoSelecionado
              ? ` - ${anoSelecionado}`
              : ''}
          </p>
        </div>

        {renderizarTabelaPoomsae()}
      </div>
    );
  };

  const renderizarConteudoAcademias = () => {
    if (abaAtiva !== ABAS.ACADEMIAS) {
      return null;
    }

    return (
      <div className="ranking-content">
        <div className="ranking-heading">
          <h1>
            Ranking Geral de Academias
          </h1>

          <p>
            Somatório da pontuação dos atletas
            {anoSelecionado
              ? ` - ${anoSelecionado}`
              : ''}
          </p>
        </div>

        {renderizarTabelaAcademias()}
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <link
          rel="icon"
          href="./img/icon.png"
        />

        <title>Ranking - Fetron</title>
      </Helmet>

      <div id="mainpreloader">
        <Preloader />
      </div>

      <div className="home dark-scheme">
        <header id="header-wrap">
          <Navbar />
        </header>

        <section className="no-bg">
          <div>
            <img
              src="../../img/background/bg-header.png"
              width="100%"
              alt="Ranking Fetron"
            />
          </div>

          <div className="container z-9">
            <div className="row align-items-center">
              <div className="col-lg-12 text-center">
                <h3 className="titulo">
                  Ranking de Atletas
                </h3>
              </div>
            </div>
          </div>
        </section>

        <section className="ranking-page">
          <div className="container">
            <div className="ranking-toolbar">
              <div className="ranking-toolbar-top">
                <div className="ranking-toolbar-title">
                  <span>
                    Filtros do ranking
                  </span>

                  <h2>
                    Consulte a classificação
                  </h2>
                </div>

                <div className="ranking-filter-field ranking-year-field">
                  <label htmlFor="ranking-ano">
                    Ano
                  </label>

                  <select
                    id="ranking-ano"
                    value={anoSelecionado}
                    onChange={(event) =>
                      setAnoSelecionado(
                        event.target.value
                      )
                    }
                    disabled={isLoadingAnos}
                  >
                    <option value="">
                      Selecione
                    </option>

                    {anos.map((ano) => (
                      <option
                        key={ano}
                        value={ano}
                      >
                        {ano}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {renderizarAbas()}
              {renderizarFiltrosGyeorugi()}
              {renderizarFiltrosPoomsae()}
              {renderizarAcoesAcademias()}
            </div>

            {renderizarConteudoGyeorugi()}
            {renderizarConteudoPoomsae()}
            {renderizarConteudoAcademias()}
          </div>
        </section>

        <Footer />
      </div>

      <ScrollToTopBtn />
    </>
  );
}